import { useEffect, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import {
  useLeagueClassicChampionList,
  getLeagueClassicChampionSlug,
  getLeagueClassicChampionImg,
} from "@ugg/shared/hooks/league-classic-champion-detail";
import { getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";

export function ChampionHomeClassic({ className }: { className?: string }) {
  const championGrid = useChampionGrid();

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classNames("w-full", className)}>
      <div
        className={`
        grid gap-[18px] max-sm:gap-[12px]
        grid-cols-[repeat(auto-fill,minmax(100px,1fr))] [&>*]:max-w-[100px]
        max-sm:grid-cols-[repeat(auto-fill,minmax(80px,1fr))] max-sm:[&>*]:max-w-[80px]
        max-xs:grid-cols-[repeat(auto-fill,minmax(60px,1fr))] max-xs:[&>*]:max-w-[60px]
        p-[18px] max-sm:p-[12px] rounded-[3px] bg-purple-400
      `}
      >
        {championGrid}
      </div>
    </div>
  );
}

function useChampionGrid() {
  const { data } = useLeagueClassicChampionList();

  const sortedChampions = useMemo(() => {
    // Manifest entries are [slugId, displayName], keyed by champion id.
    return Object.entries(data || {})
      .map(([id, [slugId, name]]) => ({
        championId: Number(id),
        name,
        slug: getLeagueClassicChampionSlug(slugId),
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [data]);

  return useMemo(() => {
    // Links to the (placeholder) league classic build page. Each cell keeps
    // the same sizing + hover zoom as the standard champion grid.
    return sortedChampions.map((champion) => (
      <Link className="group w-full mx-auto" key={champion.championId} to={getClassicChampionBuildUrl(champion.slug)}>
        <div className="flex items-center justify-center aspect-square rounded-[3px] overflow-hidden">
          <div className="flex items-center justify-center flex-none w-[115%] overflow-hidden">
            <img
              className="w-full scale-[0.9] group-hover:scale-[.95] transition-[transform] duration-[250ms]"
              src={getLeagueClassicChampionImg(champion.championId)}
              alt={champion.name}
            />
          </div>
        </div>
        <div className="group-hover:font-bold mt-[8px] text-[12px] text-white font-medium truncate">{champion.name}</div>
      </Link>
    ));
  }, [sortedChampions]);
}
